import { t } from '@lingui/core/macro';
import { convertCurrencyToNumber } from 'utils/currencyConverter';
import * as yup from 'yup';
import {
  dayRegex,
  isOver18,
  isTradingDateInFuture,
  monthRegex,
} from '../SignupForm/SignUpFormHelper';
import { isValidNumber } from './helpers/PhoneNumberHelper';

const postcodeRegex = /^[0-9]{2}[0-9]{3}$/;

const DaySchema = yup
  .string()
  .required(() => t`Needed`)
  .matches(dayRegex, () => t`Invalid entry`);

const MonthSchema = yup
  .string()
  .required(() => t`Needed`)
  .matches(monthRegex, () => t`Invalid entry`);

const YearSchema = yup
  .string()
  .min(4, () => t`Needed`)
  .max(4, () => t`Invalid entry`)
  .required(() => t`Needed`)
  .test(
    'Is 18 or over',
    () => t`You must be 18 years or older`,
    function (value, testContext) {
      if (
        !Number(testContext.parent.dobMonth) ||
        !Number(testContext.parent.dobDay)
      ) {
        // Don't check age unless day + month entered
        return true;
      }
      return isOver18(
        value,
        testContext.parent.dobMonth,
        testContext.parent.dobDay,
      );
    },
  );

const AddressSchema = yup.object().shape({
  house_name: yup.string(),
  house_number: yup.string(),
  street_line_1: yup.string().required(() => t`Needed`),
  street_line_2: yup.string(),
  town: yup.string().required(() => t`Needed`),
  postcode: yup
    .string()
    .matches(postcodeRegex, () => t`Invalid entry`)
    .required(() => t`Needed`),
});

const PeopleSchema = yup.object().shape({
  title: yup.string().required(() => t`Needed`),
  firstName: yup.string().required(() => t`Needed`),
  lastName: yup.string().required(() => t`Needed`),
  dobDay: DaySchema,
  dobMonth: MonthSchema,
  dobYear: YearSchema,
  phoneNumber: yup
    .string()
    .required(() => t`Needed`)
    .transform(function (currentValue) {
      return currentValue.replace(/\(|\)|\s+|-|\//g, '');
    })
    .test(
      'Is a valid number',
      () => t`Invalid entry`,
      function (value) {
        return isValidNumber(value);
      },
    ),
  secondaryPhoneNumber: yup
    .string()
    .transform(function (currentValue) {
      return currentValue.replace(/\(|\)|\s+|-|\//g, '');
    })
    .test(
      'Is a valid number',
      () => t`Invalid entry`,
      function (value) {
        return isValidNumber(value);
      },
    ),
  email: yup
    .string()
    .email(() => t`Invalid entry`)
    .required(() => t`Needed`),
  residentialAddresses: yup.array().of(AddressSchema).ensure().required(),
  privacyPolicyAgreement: yup
    .boolean()
    .required(() => t`Needed`)
    .oneOf([true], () => t`The terms and conditions must be accepted.`),
  roles: yup.array().of(yup.string()).ensure(),
});

export const CompanySchema = yup.object().shape({
  isManualInput: yup.boolean(),

  crefoId: yup.string().when('isManualInput', {
    is: (value: boolean) => value !== true,
    then: (schema) => schema.required(() => t`Needed`),
  }),

  name: yup.string().when('isManualInput', {
    is: true,
    then: (schema) => schema.required(() => t`Needed`),
  }),
  street: yup.string().when('isManualInput', {
    is: true,
    then: (schema) => schema.required(() => t`Needed`),
  }),
  buildingNumber: yup.string(),
  postcode: yup.string().when('isManualInput', {
    is: true,
    then: (schema) => schema.required(() => t`Needed`),
  }),
  town: yup.string().when('isManualInput', {
    is: true,
    then: (schema) => schema.required(() => t`Needed`),
  }),
  legalForm: yup.string().when('isManualInput', {
    is: true,
    then: (schema) => schema.required(() => t`Needed`),
  }),
  tradingFromMonth: yup.string().required(() => t`Needed`),
  tradingFromYear: yup
    .string()
    .required(() => t`Needed`)
    .test(
      'Trading date is not in the future',
      () => t`Trading date cannot be in the future`,
      function (value, testContext) {
        const month = testContext.parent.tradingFromMonth;
        if (!value || !month) {
          // Don't check the date unless month + year are both entered
          return true;
        }
        return !isTradingDateInFuture(month, value);
      },
    ),
  last12MonthsTurnover: yup.string().required(() => t`Needed`),
  website: yup
    .string()
    .matches(
      /^$|((http|https):\/\/)?[a-zA-Z0-9./?:@\-_=#]+\.([a-zA-Z0-9&./?:@\-_=#])*/,
      () => t`Invalid entry`,
    ),
});

const minimumDEData = {
  applicant: PeopleSchema,
  guarantors: yup.array().of(PeopleSchema).ensure(),
  company: CompanySchema,
  amount: yup
    .string()
    .required(() => t`Needed`)
    .test(
      'minCurrencyAmount',
      () => t`Between £1,000 and £500,000`,
      (value) => {
        const numericValue = convertCurrencyToNumber(value, true);
        if (!numericValue) return false;
        return numericValue >= 1000;
      },
    )
    .test(
      'maxCurrencyAmount',
      () => t`Any amount up to £500,000`,
      (value) => {
        const numericValue = convertCurrencyToNumber(value, true);
        if (!numericValue) return false;
        return numericValue <= 500000;
      },
    ),
  loanDuration: yup
    .string()
    .oneOf(['12-months', '24-months', '60-months'], () => t`Needed`)
    .required(() => t`Needed`),
  loanPurpose: yup
    .string()
    .required(() => t`Needed`)
    .notOneOf(
      ['personal_loan'],
      () => t`We are unable to provide personal loans.`,
    ),
};

export const schemaDE = yup.object().shape({
  ...minimumDEData,
  loanPurposeDetails: yup.string().when('loanPurpose', {
    is: 'other',
    then: (schema) => schema.required(() => t`Needed`),
  }),
});

export const minimumDataRequirementsSchemaDE = yup
  .object()
  .shape(minimumDEData);
